import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit — the gable end segment: the roof-gable panes plus a triangular
// plaster gable wall filling the +X end under the slopes (the triangle is
// approximated by three stacked boxes narrowing upward). Place it as the
// terminal tile of a gable run; plain roof-gable tiles continue toward -X.
// Eaves at y=0; the consumer lifts it onto 2.6 m walls.
const SHINGLE = "#4a4a4e";
const RIDGE = "#5a5a5e";
const PLASTER = "#e8e4dc";

const RISE = 0.8;
const RUN = 0.5;
const PITCH = Math.atan2(RISE, RUN);
const PANE_LEN = Math.hypot(RISE, RUN);
// Gable wall: 0.12 thick, face flush with the +X tile edge.
const WALL_X = 0.5 - 0.06;

export const roofGableEnd = defineModel({
  id: "roof-gable-end",
  batchId: "modern",
  name: "Gable roof end",
  tags: ["modern", "outdoor", "structure", "roof"],
  summary: "A gable roof segment with a plaster gable end wall at +X.",
  description:
    "The terminal gable tile of the Modern Living kit: the roof-gable panes " +
    "plus a triangular plaster end wall (stepped boxes) closing the +X end. " +
    "Chain plain roof-gable tiles toward -X. Eaves at y=0; the consumer " +
    "lifts it onto 2.6 m walls.",
  tiles: { w: 1, h: 1 },
  footprint: 0.9,
  parts(): Part[] {
    // Stepped triangle: three boxes narrowing upward, each sized to stay
    // under the pane undersides (y = 0.8 - 1.6|z| - 0.057) so no corner
    // pokes through the shingles.
    const bands: Array<[yBottom: number, height: number, width: number]> = [
      [0, 0.24, 0.62],
      [0.24, 0.21, 0.36],
      [0.45, 0.17, 0.15],
    ];
    const steps: Part[] = bands.map(([yBottom, h, width]) => ({
      geometry: new THREE.BoxGeometry(0.12, h, width),
      color: PLASTER,
      position: [WALL_X, yBottom + h / 2, 0],
    }));
    return [
      { geometry: new THREE.BoxGeometry(1.0, 0.06, PANE_LEN), color: SHINGLE,
        position: [0, RISE / 2, RUN / 2], rotation: [PITCH, 0, 0] },
      { geometry: new THREE.BoxGeometry(1.0, 0.06, PANE_LEN), color: SHINGLE,
        position: [0, RISE / 2, -RUN / 2], rotation: [-PITCH, 0, 0] },
      { geometry: new THREE.BoxGeometry(1.0, 0.05, 0.14), color: RIDGE, position: [0, RISE + 0.02, 0] },
      ...steps,
    ];
  },
  animations: [],
});
